import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supplierRequestService } from '../../services/supplierRequestService';
import { workOrderService } from '../../services/workOrderService';

const SupplierCoordination = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [workOrder, setWorkOrder] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [supplierRequest, setSupplierRequest] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load data from navigation state
    if (location?.state) {
      setWorkOrder(location?.state?.workOrder);
      setQuoteItems(location?.state?.quoteItems || []);
      setSupplierRequest(location?.state?.supplierRequest);
    }
  }, [location]);

  const handleSendToSuppliers = async () => {
    setLoading(true);
    try {
      // Update status to 'sent'
      if (supplierRequest?.id) {
        await supplierRequestService?.updateStatus(supplierRequest?.id, 'sent');
      }
      alert('Solicitud enviada a proveedores vía WhatsApp');
      setSupplierRequest(prev => ({ ...prev, status: 'sent' }));
    } catch (error) {
      console.error('Error sending to suppliers:', error);
      alert('Error al enviar la solicitud. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToQuotation = () => {
    navigate('/quotation-management');
  };

  const handleAuthorizeWork = async () => {
    if (!workOrder?.id) {
      alert('No se encontró información de la orden de trabajo');
      return;
    }

    setLoading(true);
    try {
      // Update work order status to 'aprobado_proveedores'
      await workOrderService?.updateStatus(workOrder?.id, 'aprobado_proveedores');

      // Create timeline event for authorization
      await workOrderService?.createTimelineEvent({
        workOrderId: workOrder?.id,
        stage: 'coordinacion_proveedores',
        status: 'aprobado_proveedores',
        findings: 'Trabajo autorizado por coordinación de proveedores'
      });

      alert('Trabajo autorizado exitosamente');
      
      // Navigate to Workshop Dashboard with folio parameter
      navigate(`/workshop-dashboard?folio=${workOrder?.folioOt || workOrder?.id}`);
    } catch (error) {
      console.error('Error authorizing work:', error);
      alert('Error al autorizar el trabajo. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartWork = async () => {
    if (!workOrder?.id) {
      alert('No se encontró información de la orden de trabajo');
      return;
    }

    setLoading(true);
    try {
      // Update work order status to 'en_proceso'
      await workOrderService?.updateStatus(workOrder?.id, 'en_proceso');

      // Create timeline event for work initiation
      await workOrderService?.createTimelineEvent({
        workOrderId: workOrder?.id,
        stage: 'ejecucion_trabajo',
        status: 'en_proceso',
        findings: 'Trabajo iniciado - Items de cotización cargados para ejecución'
      });

      // Navigate to Work Execution with pre-loaded quote items
      navigate('/work-execution', {
        state: {
          workOrder,
          quoteItems,
          supplierRequest
        }
      });
    } catch (error) {
      console.error('Error starting work:', error);
      alert('Error al iniciar el trabajo. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = quoteItems?.reduce((sum, item) => sum + (item?.price || 0) * (item?.quantity || 1), 0) || 0;

  return (
    <>
      <Helmet>
        <title>Coordinación de Proveedores - GearControl</title>
      </Helmet>
      <div className="flex h-screen overflow-hidden bg-background">
        <MainSidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="container mx-auto px-4 py-6 md:py-8 max-w-7xl">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Icon name="Users" size={32} className="text-accent" />
                    <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                      Coordinación de Proveedores
                    </h1>
                  </div>
                  <Button
                    variant="outline"
                    iconName="ArrowLeft"
                    iconPosition="left"
                    onClick={handleBackToQuotation}
                  >
                    Volver a Cotización
                  </Button>
                </div>
                <p className="text-muted-foreground">
                  Gestione solicitudes de cotización y comunicación con proveedores
                </p>
              </div>

              {/* Work Order Info */}
              {workOrder && (
                <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Icon name="FileText" size={32} className="text-primary" />
                      <h2 className="text-2xl font-heading font-semibold text-foreground">
                        Información de Orden de Trabajo
                      </h2>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        iconName="MessageSquare"
                        iconPosition="left"
                        disabled={loading}
                        onClick={() => {}}
                      >
                        Abrir Chat de WhatsApp
                      </Button>
                      <Button
                        variant="default"
                        iconName="CheckCircle"
                        iconPosition="left"
                        onClick={handleAuthorizeWork}
                        disabled={loading || !workOrder?.id}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Autorizar Trabajo
                      </Button>
                      <Button
                        variant="default"
                        iconName="Play"
                        iconPosition="left"
                        onClick={handleStartWork}
                        disabled={loading || !workOrder?.id}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Iniciar Trabajo
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Folio</p>
                      <p className="text-base font-semibold text-foreground">{workOrder?.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Vehículo</p>
                      <p className="text-base font-semibold text-foreground">{workOrder?.vehicle}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Cliente</p>
                      <p className="text-base font-semibold text-foreground">{workOrder?.client}</p>
                    </div>
                    {workOrder?.vin && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">VIN</p>
                        <p className="text-base font-semibold text-foreground font-mono">{workOrder?.vin}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Supplier Request Info */}
              {supplierRequest && (
                <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Package" size={24} className="text-accent" />
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                      Solicitud de Proveedores
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Número de Solicitud</p>
                      <p className="text-base font-semibold text-foreground">{supplierRequest?.requestNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Estado</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        supplierRequest?.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                        supplierRequest?.status === 'responded' ? 'bg-green-100 text-green-800' :
                        supplierRequest?.status === 'completed'? 'bg-purple-100 text-purple-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {supplierRequest?.status === 'pending' ? 'Pendiente' :
                         supplierRequest?.status === 'sent' ? 'Enviado' :
                         supplierRequest?.status === 'responded' ? 'Respondido' :
                         supplierRequest?.status === 'completed' ? 'Completado' : 'Cancelado'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Items</p>
                      <p className="text-base font-semibold text-foreground">{supplierRequest?.totalItems}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Monto Total</p>
                      <p className="text-base font-semibold text-foreground">
                        ${supplierRequest?.totalAmount?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quote Items List */}
              <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="List" size={24} className="text-primary" />
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                    Items de Cotización ({quoteItems?.length})
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Descripción</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Cantidad</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Precio Unit.</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Total</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Proveedor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quoteItems?.map((item, index) => (
                        <tr key={index} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 px-4 text-sm text-foreground">{item?.description}</td>
                          <td className="py-3 px-4 text-sm text-center text-foreground">{item?.quantity || 1}</td>
                          <td className="py-3 px-4 text-sm text-right text-foreground">
                            ${item?.price?.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-semibold text-foreground">
                            ${((item?.price || 0) * (item?.quantity || 1))?.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">{item?.supplier || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border">
                        <td colSpan="3" className="py-3 px-4 text-right font-semibold text-foreground">
                          Total:
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-lg text-accent">
                          ${totalAmount?.toFixed(2)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Zap" size={24} className="text-accent" />
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                    Acciones de Coordinación
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    variant="default"
                    iconName="Send"
                    iconPosition="left"
                    onClick={handleSendToSuppliers}
                    disabled={loading || supplierRequest?.status === 'sent'}
                    fullWidth
                  >
                    {supplierRequest?.status === 'sent' ? 'Enviado a Proveedores' : 'Enviar a Proveedores'}
                  </Button>
                  <Button
                    variant="outline"
                    iconName="MessageSquare"
                    iconPosition="left"
                    disabled={loading}
                    fullWidth
                  >
                    Abrir Chat de WhatsApp
                  </Button>
                  <Button
                    variant="default"
                    iconName="CheckCircle"
                    iconPosition="left"
                    onClick={handleAuthorizeWork}
                    disabled={loading || !workOrder?.id}
                    fullWidth
                    className="md:col-span-2 bg-green-600 hover:bg-green-700"
                  >
                    Autorizar Trabajo
                  </Button>
                  <Button
                    variant="default"
                    iconName="Play"
                    iconPosition="left"
                    onClick={handleStartWork}
                    disabled={loading || !workOrder?.id}
                    fullWidth
                    className="md:col-span-2 bg-blue-600 hover:bg-blue-700"
                  >
                    Iniciar Trabajo
                  </Button>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-heading font-semibold text-foreground mb-2">
                        Información de Coordinación
                      </h3>
                      <ul className="space-y-1 text-xs caption text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>La solicitud se enviará a los grupos de WhatsApp de proveedores</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>Los proveedores responderán con sus cotizaciones</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>Puede gestionar las respuestas desde esta pantalla</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupplierCoordination;